import { useEffect, useState } from 'react';

/**
 * Returns `false` until the browser is idle, then `true`. Backed by
 * `requestIdleCallback` so work gated on it yields to everything the browser is
 * doing — rendering, the active view's requests, user interaction — and only
 * runs when there's genuine spare capacity. On slow/older machines that stay
 * busy, it falls back to firing after `timeout` ms (during a lull), so it never
 * blocks and never competes with the work the clinician is actually waiting on.
 *
 * Used to defer non-critical background prefetching (e.g. warming the Summary
 * distribution graphs) so it can't cause perf issues on constrained hardware.
 */
export const useIdleReady = (timeout = 3000): boolean => {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const w = window as unknown as {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    if (typeof w.requestIdleCallback === 'function') {
      const id = w.requestIdleCallback(() => setReady(true), { timeout });
      return () => w.cancelIdleCallback?.(id);
    }
    const t = window.setTimeout(() => setReady(true), timeout);
    return () => window.clearTimeout(t);
  }, [timeout]);
  return ready;
};
